import { useState, useRef, useEffect, useMemo } from "react";
import { Star, Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";

interface WriteReviewFormProps {
  productId: string;
  productHandle: string;
  onSubmitted: () => void;
  onCancel: () => void;
}

const SESSION_KEY = "ll_review_session_id";

function getReviewSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID().replace(/-/g, "");
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID().replace(/-/g, "");
  }
}

export const WriteReviewForm = ({
  productId,
  productHandle,
  onSubmitted,
  onCancel,
}: WriteReviewFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedAt = useMemo(() => Date.now(), []);
  const sessionId = useMemo(() => getReviewSessionId(), []);

  // Prefill from the profile when someone happens to be logged in.
  useEffect(() => {
    let active = true;
    getSupabase().then(async (supabase) => {
      const { data } = await supabase.auth.getUser();
      if (!active) return;
      const u = data.user ?? null;
      setUser(u);
      setAuthChecked(true);
      if (!u) return;
      setEmail((prev) => prev || u.email || "");
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", u.id)
        .maybeSingle();
      if (!active) return;
      const displayName =
        profile?.display_name || u.user_metadata?.full_name || u.email?.split("@")[0] || "";
      setName((prev) => prev || displayName);
    });
    return () => {
      active = false;
    };
  }, []);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    const remaining = 4 - photos.length;
    const toAdd = validFiles.slice(0, remaining);

    setPhotos((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoPreviews((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const nameValid = name.trim().length >= 2;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a star rating");
      return;
    }
    if (!nameValid) {
      toast.error("Please enter your name");
      return;
    }
    if (!emailValid) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (body.trim().length < 10) {
      toast.error("Review must be at least 10 characters");
      return;
    }
    if (body.trim().length > 2000) {
      toast.error("Review must be under 2000 characters");
      return;
    }
    if (Date.now() - mountedAt < 5000) {
      toast.error("Please take a moment to finish your review before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const supabase = await getSupabase();
      // Photos: user-scoped path when signed in, session-scoped path otherwise.
      const folder = user ? user.id : `anon/${sessionId}`;
      const photoUrls: string[] = [];
      for (const file of photos) {
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase().slice(0, 5);
        const path = `${folder}/${productHandle}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("review-images")
          .upload(path, file);
        if (uploadError) {
          console.error("Upload error:", uploadError);
          continue;
        }
        const { data: urlData } = supabase.storage.from("review-images").getPublicUrl(path);
        photoUrls.push(urlData.publicUrl);
      }

      const { data, error } = await supabase.functions.invoke("submit-review", {
        body: {
          productId,
          productHandle,
          rating,
          title: title.trim().slice(0, 200),
          bodyText: body.trim().slice(0, 2000),
          authorName: name.trim().slice(0, 100),
          authorEmail: email.trim().toLowerCase(),
          photos: photoUrls,
          sessionId,
          honeypot,
          elapsedMs: Date.now() - mountedAt,
        },
      });

      const errorMessage =
        (data as any)?.error ||
        (error ? "We couldn't save your review. Please try again." : null);

      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }

      toast.success("Thank you — your review has been sent for review", {
        description:
          "It isn't published yet. We read every review before it appears on the product page.",
      });
      onSubmitted();
    } catch (err: any) {
      console.error("Submit review error:", err);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className="border border-border rounded-lg p-5 md:p-6 bg-secondary/30">
      <h3 className="text-lg font-semibold text-foreground mb-1">Write a Review</h3>
      <p className="text-[12px] text-muted-foreground mb-4">
        No account needed. Every review is read by a person before it appears on the site.
      </p>

      {/* Star selection */}
      <div className="mb-4">
        <label className="text-sm font-medium text-foreground mb-2 block">
          Rating <span className="text-destructive">*</span>
        </label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              aria-label={`Rate ${i} out of 5 stars`}
              onMouseEnter={() => setHoverRating(i)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(i)}
              className="p-0.5 transition-transform hover:scale-110"
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  i <= displayRating
                    ? "text-amber-500 fill-amber-500"
                    : "text-muted-foreground/30"
                }`}
              />
            </button>
          ))}
          {displayRating > 0 && (
            <span className="ml-2 text-sm text-muted-foreground self-center">
              {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][displayRating]}
            </span>
          )}
        </div>
      </div>

      {/* Name + email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="review-name" className="text-sm font-medium text-foreground mb-1.5 block">
            Your name <span className="text-destructive">*</span>
          </label>
          <input
            id="review-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name or initials"
            maxLength={100}
            autoComplete="name"
            className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Shown with your review.</p>
        </div>
        <div>
          <label htmlFor="review-email" className="text-sm font-medium text-foreground mb-1.5 block">
            Email <span className="text-destructive">*</span>
          </label>
          <input
            id="review-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            maxLength={255}
            autoComplete="email"
            className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Never shown publicly. We only use it to contact you about your review.
          </p>
        </div>
      </div>

      {/* Honeypot — hidden from real users */}
      <div className="absolute w-px h-px overflow-hidden -left-[9999px]" aria-hidden="true">
        <label htmlFor="review-website">Website</label>
        <input
          id="review-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {/* Title */}
      <div className="mb-4">
        <label htmlFor="review-title" className="text-sm font-medium text-foreground mb-1.5 block">
          Headline
        </label>
        <input
          id="review-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sum up your experience"
          maxLength={200}
          className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Body */}
      <div className="mb-4">
        <label htmlFor="review-body" className="text-sm font-medium text-foreground mb-1.5 block">
          Your Review <span className="text-destructive">*</span>
        </label>
        <textarea
          id="review-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What did you like or dislike? How do you use this product?"
          rows={4}
          maxLength={2000}
          className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <p className="text-[11px] text-muted-foreground mt-1">{body.length}/2000</p>
      </div>

      {/* Photos */}
      <div className="mb-5">
        <label className="text-sm font-medium text-foreground mb-1.5 block">
          Photos (optional, max 4)
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handlePhotoSelect}
        />
        <div className="flex gap-2 flex-wrap">
          {photoPreviews.map((preview, i) => (
            <div key={i} className="relative w-16 h-16">
              <img
                src={preview}
                alt={`Upload ${i + 1}`}
                className="w-full h-full object-cover rounded border border-border"
              />
              <button
                type="button"
                aria-label={`Remove photo ${i + 1}`}
                onClick={() => removePhoto(i)}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {photos.length < 4 && (
            <button
              type="button"
              aria-label="Add a photo to your review"
              onClick={() => fileInputRef.current?.click()}
              className="w-16 h-16 border-2 border-dashed border-border rounded flex items-center justify-center hover:border-primary/40 transition-colors"
            >
              <Upload className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={
            submitting ||
            !authChecked ||
            rating === 0 ||
            !nameValid ||
            !emailValid ||
            body.trim().length < 10
          }
          className="rounded-full h-[44px] px-8 text-[12px] tracking-[0.1em] uppercase font-bold"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Submitting…
            </>
          ) : (
            "Submit Review"
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-full h-[44px] px-6 text-[12px] tracking-[0.1em] uppercase font-bold"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
